(() => {
  const sectionSelector = '[data-communite-device-finder]'
  const storageKey = 'communite:selected-device:v1'
  const urlParameter = 'cm_device'
  const instances = new WeakMap()

  const translations = () => ({
    modelPlaceholder: window.theme?.locales?.device?.model_placeholder || 'Select a model',
    modelEmpty: window.theme?.locales?.device?.model_empty || 'No models available',
    detailPlaceholder: window.theme?.locales?.device?.detail_placeholder || 'Select a generation or size',
    needWaiting: window.theme?.locales?.device?.need_waiting || 'Select a device first',
    match: window.theme?.locales?.device?.match || 'Compatible with your selected device: [device]',
    mismatch: window.theme?.locales?.device?.mismatch || '[device] is not in this product’s compatibility list.',
    fallback: window.theme?.locales?.device?.fallback || 'Verify compatibility with [device] on the product page.',
    unknown: window.theme?.locales?.device?.unknown || 'Compatibility data is not available for [device].'
  })

  const replaceToken = (template, token, value) => template.split(token).join(value)

  const readStorage = () => {
    try {
      const value = window.localStorage.getItem(storageKey)
      if (!value) return null

      const parsed = JSON.parse(value)
      if (!parsed || typeof parsed.key !== 'string' || !parsed.key) return null
      return parsed
    } catch {
      return null
    }
  }

  const writeStorage = (value) => {
    try {
      if (value) {
        window.localStorage.setItem(storageKey, JSON.stringify(value))
      } else {
        window.localStorage.removeItem(storageKey)
      }
    } catch {
      // Selection still works for the current page when storage is unavailable.
    }
  }

  const readUrlKey = () => {
    try {
      return new URL(window.location.href).searchParams.get(urlParameter) || ''
    } catch {
      return ''
    }
  }

  const writeUrlKey = (key = '') => {
    try {
      const url = new URL(window.location.href)

      if (key) {
        url.searchParams.set(urlParameter, key)
      } else {
        url.searchParams.delete(urlParameter)
      }

      window.history.replaceState(window.history.state, '', url)
    } catch {
      // Storage remains the persistence fallback when URL state is unavailable.
    }
  }

  const humanizeKey = (key) => key
    .split('-')
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')

  let selectedDevice = readStorage()
  const urlKey = readUrlKey()

  if (urlKey && selectedDevice?.key !== urlKey) {
    selectedDevice = {
      key: urlKey,
      label: humanizeKey(urlKey),
      brand: '',
      model: '',
      generation: '',
      size: '',
      needKey: 'all',
      needLabel: ''
    }
    writeStorage(selectedDevice)
  }

  const updateDeviceLabels = (root = document) => {
    root.querySelectorAll?.('[data-cm-device-label]').forEach(label => {
      if (!label.dataset.cmDefaultLabel) {
        label.dataset.cmDefaultLabel = label.textContent.trim()
      }

      label.textContent = selectedDevice?.label || label.dataset.cmDefaultLabel
      label.closest('[data-cm-device-link]')?.classList.toggle('has-device', Boolean(selectedDevice))
    })
  }

  const updateCompatibility = (root = document) => {
    const copy = translations()

    root.querySelectorAll?.('[data-cm-product-compatibility]').forEach(container => {
      const copyElement = container.querySelector('[data-cm-compatibility-copy]')
      if (!copyElement) return

      if (!container.dataset.cmDefaultCopy) {
        container.dataset.cmDefaultCopy = copyElement.textContent.trim()
      }

      const hasCompatibility = container.dataset.cmHasCompatibility === 'true'
      const keys = (container.dataset.cmCompatibleDeviceKeys || '').split('|').filter(Boolean)

      container.classList.remove('is-match', 'is-mismatch', 'is-unknown')

      if (!selectedDevice) {
        container.hidden = !hasCompatibility
        copyElement.textContent = container.dataset.cmDefaultCopy
        return
      }

      container.hidden = false

      if (keys.length > 0 && keys.includes(selectedDevice.key)) {
        container.classList.add('is-match')
        copyElement.textContent = replaceToken(copy.match, '[device]', selectedDevice.label)
      } else if (keys.length > 0) {
        container.classList.add('is-mismatch')
        copyElement.textContent = replaceToken(copy.mismatch, '[device]', selectedDevice.label)
      } else if (hasCompatibility) {
        container.classList.add('is-unknown')
        copyElement.textContent = replaceToken(copy.fallback, '[device]', selectedDevice.label)
      } else {
        container.classList.add('is-unknown')
        copyElement.textContent = replaceToken(copy.unknown, '[device]', selectedDevice.label)
      }
    })
  }

  const applyState = (root = document) => {
    document.documentElement.toggleAttribute('data-cm-device-selected', Boolean(selectedDevice))

    if (selectedDevice) {
      document.documentElement.dataset.cmDeviceKey = selectedDevice.key
    } else {
      delete document.documentElement.dataset.cmDeviceKey
    }

    updateDeviceLabels(root)
    updateCompatibility(root)
  }

  const deviceState = {
    get current () {
      return selectedDevice ? { ...selectedDevice } : null
    },

    select (device) {
      selectedDevice = { ...device }
      writeStorage(selectedDevice)
      writeUrlKey(selectedDevice.key)
      applyState(document)
      document.dispatchEvent(new CustomEvent('cm:device:change', {
        detail: { device: this.current }
      }))
    },

    clear () {
      selectedDevice = null
      writeStorage(null)
      writeUrlKey()
      applyState(document)
      document.dispatchEvent(new CustomEvent('cm:device:change', {
        detail: { device: null }
      }))
    },

    apply: applyState,

    destination (url, deviceKey) {
      try {
        const destination = new URL(url, window.location.origin)
        destination.searchParams.set(urlParameter, deviceKey)
        return destination.toString()
      } catch {
        return url
      }
    }
  }

  class DeviceFinder {
    constructor (section) {
      this.section = section
      this.controller = new AbortController()
      this.form = section.querySelector('[data-device-form]')
      this.brandSelect = section.querySelector('[data-device-brand]')
      this.modelSelect = section.querySelector('[data-device-model]')
      this.detailSelect = section.querySelector('[data-device-detail]')
      this.detailField = section.querySelector('[data-device-detail-field]')
      this.needSelect = section.querySelector('[data-device-need]')
      this.needStepIndex = section.querySelector('[data-device-need-index]')
      this.submitButton = section.querySelector('[data-device-submit]')
      this.changeButton = section.querySelector('[data-device-change]')
      this.selection = section.querySelector('[data-device-selection]')
      this.selectionName = section.querySelector('[data-device-selection-name]')
      this.status = section.querySelector('[data-device-status]')
      this.modelTemplate = section.querySelector('[data-device-model-options]')
      this.needTemplate = section.querySelector('[data-device-need-options]')
      this.preview = section.querySelector('[data-device-preview]')
      this.fallbackPreview = section.querySelector('[data-device-fallback-preview]')
      this.currentEntry = null
      this.destination = ''

      if (
        !this.form ||
        !this.brandSelect ||
        !this.modelSelect ||
        !this.detailSelect ||
        !this.needSelect ||
        !this.submitButton ||
        !this.status ||
        !this.modelTemplate ||
        !this.needTemplate
      ) return

      this.entries = Array.from(this.modelTemplate.content.querySelectorAll('option'))
      this.bind()
      this.reset()
      this.restore()
    }

    bind () {
      const signal = this.controller.signal

      this.brandSelect.addEventListener('change', () => this.onBrandChange(), { signal })
      this.modelSelect.addEventListener('change', () => this.onModelChange(), { signal })
      this.detailSelect.addEventListener('change', () => this.onDetailChange(), { signal })
      this.needSelect.addEventListener('change', () => this.onNeedChange(), { signal })
      this.changeButton?.addEventListener('click', () => {
        deviceState.clear()
        this.reset()
        this.brandSelect.focus()
      }, { signal })
      this.form.addEventListener('submit', event => this.onSubmit(event), { signal })
      document.addEventListener('cm:device:change', () => {
        this.reset()
        this.restore()
      }, { signal })
    }

    setStatus (message, isError = false) {
      this.status.textContent = message
      this.status.classList.toggle('communite-device-finder__status--error', isError)
    }

    replaceOptions (select, placeholder) {
      select.replaceChildren()
      const option = document.createElement('option')
      option.value = ''
      option.textContent = placeholder
      select.append(option)
    }

    renderPreview (entryId = '') {
      if (!this.preview || !this.fallbackPreview) return

      const selectedPreview = Array.from(this.section.querySelectorAll('[data-device-preview-template]'))
        .find(template => template.dataset.devicePreviewTemplate === entryId)
      const source = selectedPreview || this.fallbackPreview

      this.preview.replaceChildren(source.content.cloneNode(true))
    }

    resetDeviceSteps () {
      const copy = translations()

      this.currentEntry = null
      this.destination = ''
      this.detailField.hidden = true
      if (this.needStepIndex) this.needStepIndex.textContent = '03'
      this.detailSelect.disabled = true
      this.replaceOptions(this.detailSelect, copy.detailPlaceholder)
      this.needSelect.disabled = true
      this.replaceOptions(this.needSelect, copy.needWaiting)
      this.submitButton.disabled = true
      this.submitButton.removeAttribute('data-destination')
      this.selection.hidden = true
      this.selectionName.textContent = ''
      this.renderPreview()
    }

    reset () {
      this.brandSelect.value = ''
      this.modelSelect.disabled = true
      this.replaceOptions(this.modelSelect, translations().modelPlaceholder)
      this.resetDeviceSteps()
      this.setStatus(this.section.dataset.requiredMessage || '')
    }

    entriesForBrand () {
      return this.entries.filter(entry => entry.dataset.brand === this.brandSelect.value)
    }

    entriesForModel () {
      return this.entriesForBrand()
        .filter(entry => entry.dataset.modelKey === this.modelSelect.value)
    }

    onBrandChange () {
      const copy = translations()
      const entries = this.entriesForBrand()
      const models = new Map()

      this.replaceOptions(
        this.modelSelect,
        entries.length > 0 ? copy.modelPlaceholder : copy.modelEmpty
      )
      this.resetDeviceSteps()

      entries.forEach(entry => {
        if (!models.has(entry.dataset.modelKey)) {
          models.set(entry.dataset.modelKey, entry.dataset.modelName)
        }
      })

      models.forEach((label, value) => {
        const option = document.createElement('option')
        option.value = value
        option.textContent = label
        this.modelSelect.append(option)
      })

      this.modelSelect.disabled = entries.length === 0
      this.setStatus(this.section.dataset.requiredMessage || '', entries.length === 0 && Boolean(this.brandSelect.value))
    }

    onModelChange () {
      const entries = this.entriesForModel()
      const needsDetail = entries.length > 1 || entries.some(entry => entry.dataset.detailLabel)

      this.resetDeviceSteps()

      if (!this.modelSelect.value || entries.length === 0) return

      if (!needsDetail && entries.length === 1) {
        this.chooseEntry(entries[0])
        return
      }

      this.detailField.hidden = false
      if (this.needStepIndex) this.needStepIndex.textContent = '04'
      this.detailSelect.disabled = false
      this.replaceOptions(this.detailSelect, translations().detailPlaceholder)

      entries.forEach(entry => {
        const option = document.createElement('option')
        option.value = entry.value
        option.textContent = entry.dataset.detailLabel || entry.dataset.modelName
        this.detailSelect.append(option)
      })
    }

    onDetailChange () {
      const entry = this.entries.find(option => option.value === this.detailSelect.value)

      if (entry) {
        this.chooseEntry(entry)
      } else {
        this.currentEntry = null
        this.destination = ''
        this.needSelect.disabled = true
        this.replaceOptions(this.needSelect, translations().needWaiting)
        this.submitButton.disabled = true
        this.submitButton.removeAttribute('data-destination')
        this.selection.hidden = true
        this.selectionName.textContent = ''
        this.renderPreview()
        this.setStatus(this.section.dataset.requiredMessage || '')
      }
    }

    chooseEntry (entry) {
      this.currentEntry = entry
      this.renderPreview(entry.value)
      this.populateNeeds()
      this.selection.hidden = false
      this.selectionName.textContent = this.deviceLabel(entry)
    }

    populateNeeds () {
      this.replaceOptions(this.needSelect, translations().needWaiting)

      const options = Array.from(this.needTemplate.content.querySelectorAll('option'))
        .filter(option => Boolean(this.currentEntry.dataset[option.dataset.urlKey]))

      options.forEach(option => this.needSelect.append(option.cloneNode(true)))

      if (options.length === 0) {
        this.needSelect.disabled = true
        this.submitButton.disabled = true
        this.setStatus(this.section.dataset.filterMessage || '', true)
        return
      }

      this.needSelect.disabled = false
      this.needSelect.value = options[0].value
      this.onNeedChange()
    }

    onNeedChange () {
      const option = this.needSelect.selectedOptions[0]
      const urlKey = option?.dataset.urlKey
      const destination = urlKey && this.currentEntry ? this.currentEntry.dataset[urlKey] : ''

      this.destination = destination || ''
      this.submitButton.disabled = !this.destination

      if (!this.destination) {
        this.submitButton.removeAttribute('data-destination')
        this.setStatus(this.section.dataset.filterMessage || '', true)
        return
      }

      this.submitButton.dataset.destination = this.destination

      const readyMessage = this.section.dataset.readyMessage || '[device] / [need]'
      const message = replaceToken(
        replaceToken(readyMessage, '[device]', this.deviceLabel(this.currentEntry)),
        '[need]',
        option.textContent.trim()
      )
      this.setStatus(message)
    }

    deviceLabel (entry) {
      return [
        entry.dataset.brandName,
        entry.dataset.modelName,
        entry.dataset.generation,
        entry.dataset.size
      ].filter(Boolean).join(' ')
    }

    stateFromSelection () {
      const need = this.needSelect.selectedOptions[0]

      return {
        key: this.currentEntry.dataset.deviceKey,
        label: this.deviceLabel(this.currentEntry),
        brand: this.currentEntry.dataset.brandName || '',
        model: this.currentEntry.dataset.modelName || '',
        generation: this.currentEntry.dataset.generation || '',
        size: this.currentEntry.dataset.size || '',
        needKey: need?.value || 'all',
        needLabel: need?.textContent.trim() || '',
        destination: this.destination
      }
    }

    onSubmit (event) {
      event.preventDefault()

      if (!this.currentEntry || !this.destination) {
        this.setStatus(this.section.dataset.filterMessage || this.section.dataset.requiredMessage || '', true)
        return
      }

      const state = this.stateFromSelection()
      deviceState.select(state)
      window.location.assign(deviceState.destination(this.destination, state.key))
    }

    restore () {
      const state = deviceState.current
      if (!state) return

      const entry = this.entries.find(option => option.dataset.deviceKey === state.key)
      if (!entry) return

      this.brandSelect.value = entry.dataset.brand
      this.onBrandChange()
      this.modelSelect.value = entry.dataset.modelKey
      this.onModelChange()

      if (this.detailField.hidden === false) {
        this.detailSelect.value = entry.value
        this.onDetailChange()
      }

      const matchingNeed = Array.from(this.needSelect.options)
        .find(option => option.value === state.needKey)
      if (matchingNeed) {
        this.needSelect.value = matchingNeed.value
        this.onNeedChange()
      }
    }

    selectBlock (blockId) {
      const entry = this.entries.find(option => option.value === blockId)
      if (!entry) return

      this.brandSelect.value = entry.dataset.brand
      this.onBrandChange()
      this.modelSelect.value = entry.dataset.modelKey
      this.onModelChange()

      if (this.detailField.hidden === false) {
        this.detailSelect.value = entry.value
        this.onDetailChange()
      }
    }

    destroy () {
      this.controller.abort()
      instances.delete(this.section)
    }
  }

  const findSections = (root) => {
    const sections = []

    if (root instanceof Element && root.matches(sectionSelector)) {
      sections.push(root)
    }

    root.querySelectorAll?.(sectionSelector).forEach(section => sections.push(section))
    return sections
  }

  const init = (root = document) => {
    findSections(root).forEach(section => {
      if (!instances.has(section)) {
        instances.set(section, new DeviceFinder(section))
      }
    })
    deviceState.apply(root)
  }

  const destroy = (root) => {
    findSections(root).forEach(section => instances.get(section)?.destroy())
  }

  document.addEventListener('shopify:section:load', event => init(event.target))
  document.addEventListener('shopify:section:unload', event => destroy(event.target))
  document.addEventListener('shopify:section:select', event => {
    init(event.target)
    event.target.querySelector?.('[data-device-brand]')?.focus()
  })
  document.addEventListener('shopify:block:select', event => {
    const marker = event.target.matches?.('[data-device-block-marker]')
      ? event.target
      : event.target.querySelector?.('[data-device-block-marker]')
    const section = event.target.closest?.(sectionSelector)
    if (marker && section) {
      instances.get(section)?.selectBlock(marker.dataset.deviceBlockMarker)
    }
  })
  document.addEventListener('cm:content:updated', event => deviceState.apply(event.target))

  window.CommuniteDeviceState = deviceState
  window.CommuniteDeviceFinder = { init, destroy, version: '2' }

  init(document)
})()
